import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    ArrowLeft, Activity, Layers, Crown, Sparkles, Shield,
    Users, Zap, Heart, AlertTriangle, CheckCircle, User,
    ChevronDown, ChevronUp, ExternalLink, Film, ThumbsUp,
    BookOpen, Brain, AlertCircle, Scroll, ChevronRight
} from 'lucide-react';
import { useDevMode } from '../contexts/DevModeContext';
import { to5Scale, to10Scale, skillToNumber } from '../utils/displayConverters';
import { Pulse } from '../components/Animations';
import StatMeter from '../components/StatMeter';

const API_BASE = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';

// Section configuration with categories - defined at module level for sidebar access
const SECTION_CONFIG = {
    // UNDERSTAND IT
    appeal: { label: 'Why It Works', icon: Sparkles, color: 'text-wine-400', category: 'understand' },
    psychology: { label: 'The Psychology', icon: Brain, color: 'text-rose-400', category: 'understand' },
    intensity_levels: { label: 'Intensity Levels', icon: Activity, color: 'text-orange-400', category: 'understand' },
    experience_levels: { label: 'Experience Levels', icon: Zap, color: 'text-orange-400', category: 'understand' },
    // TRY IT
    technique: { label: 'How To Do It', icon: Zap, color: 'text-amber-400', category: 'try' },
    variations: { label: 'Variations', icon: Layers, color: 'text-purple-400', category: 'try' },
    for_dominants: { label: 'For Givers', icon: Crown, color: 'text-amber-400', category: 'try' },
    giver_experience: { label: 'Giver Experience', icon: Crown, color: 'text-amber-400', category: 'try' },
    for_submissives: { label: 'For Receivers', icon: Heart, color: 'text-pink-400', category: 'try' },
    receiver_experience: { label: 'Receiver Experience', icon: Heart, color: 'text-pink-400', category: 'try' },
    prerequisites: { label: 'Prerequisites', icon: CheckCircle, color: 'text-green-400', category: 'try' },
    anatomy_focus: { label: 'Key Anatomy', icon: Activity, color: 'text-rose-400', category: 'try' },
    rhythm_guide: { label: 'Rhythm & Pacing', icon: Activity, color: 'text-purple-400', category: 'try' },
    physical_requirements: { label: 'Physical Requirements', icon: Activity, color: 'text-orange-400', category: 'try' },
    // STAY SAFE
    risks: { label: 'Risks & Prevention', icon: AlertCircle, color: 'text-red-400', category: 'safe' },
    accepted_risks: { label: 'Risks You Accept', icon: AlertTriangle, color: 'text-orange-500', category: 'safe' },
    red_flags: { label: 'Red Flags', icon: AlertTriangle, color: 'text-red-500', category: 'safe' },
    safety: { label: 'Safety Notes', icon: Shield, color: 'text-red-400', category: 'safe' },
    ethics: { label: 'How to Talk About It', icon: Users, color: 'text-emerald-400', category: 'safe' },
    aftercare: { label: 'Aftercare', icon: Heart, color: 'text-pink-400', category: 'safe' },
    // EXPLORE MORE
    imaginary_scenarios: { label: 'Fantasy Scenarios', icon: Scroll, color: 'text-purple-400', category: 'explore' },
    related_actions: { label: 'Related Activities', icon: ThumbsUp, color: 'text-indigo-400', category: 'explore' },
    where_to_find: { label: 'Where to Find Content', icon: Film, color: 'text-pink-500', category: 'explore' },
    combinations: { label: 'Pairs Well With', icon: Users, color: 'text-blue-400', category: 'explore' },
    progression: { label: "What's Next", icon: ChevronRight, color: 'text-indigo-400', category: 'explore' },
    community: { label: 'Community & Spaces', icon: Users, color: 'text-blue-400', category: 'explore' },
    orgasm_optimization: { label: 'Orgasm Tips', icon: Heart, color: 'text-pink-500' }
};

// No hardcoded section categories - sections render dynamically from database


// Type badge component
const TypeBadge = ({ active, label, color }) => {
    if (!active) return null;
    return (
        <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide ${color}`}>
            {label}
        </span>
    );
};

// Section card for content areas with fantasy styling
const SectionCard = ({ title, icon: Icon, children, iconColor = 'text-[#C4A35A]', defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <div className="bg-[#0A0A0A]/80 border border-wine-800/30 hover:border-wine-700/40 rounded-xl overflow-hidden transition-all duration-300">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-5 hover:bg-wine-950/30 transition-colors focus:outline-none focus:ring-2 focus:ring-[#C4A35A]/30 focus:ring-inset"
            >
                <div className="flex items-center gap-3">
                    {Icon && <Icon className={`w-5 h-5 ${iconColor}`} />}
                    <h3 className="font-fantasy font-semibold text-lg text-cream/90 tracking-wide">{title}</h3>
                </div>
                <ChevronDown className={`w-5 h-5 text-cream/40 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-6 pb-6 pt-3 border-t border-wine-700/30">
                    <div className="text-cream/70 leading-relaxed">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

const SexActionPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isDevMode } = useDevMode();
    const [action, setAction] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        setLoading(true);
        fetch(`${API_BASE}/sex-actions/${id}`)
            .then(res => {
                if (!res.ok) throw new Error('Action not found');
                return res.json();
            })
            .then(data => {
                setAction(data);
                setLoading(false);
            })
            .catch(err => {
                setError(err.message);
                setLoading(false);
            });
    }, [id]);

    // SEO: Dynamic page title
    useEffect(() => {
        if (action?.name) {
            document.title = `${action.name} — Pleasance`;
        }
        return () => { document.title = 'Pleasance — Play Without Shame'; };
    }, [action]);

    if (loading) {
        return (
            <div className="h-full flex items-center justify-center bg-dark-950">
                <div className="text-dark-400 flex items-center gap-3">
                    <Pulse>
                        <Activity className="w-6 h-6" />
                    </Pulse>
                    Loading action...
                </div>
            </div>
        );
    }

    if (error || !action) {
        return (
            <div className="h-full flex flex-col items-center justify-center bg-dark-950 p-8">
                <AlertTriangle className="w-12 h-12 text-red-400 mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Action Not Found</h2>
                <p className="text-dark-400 mb-6">{error}</p>
                <button
                    onClick={() => navigate('/')}
                    className="px-6 py-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors"
                >
                    Back to Home
                </button>
            </div>
        );
    }

    // Determine intensity color
    const getIntensityColor = (val) => {
        if (val >= 8) return 'bg-wine-500';
        if (val >= 5) return 'bg-amber-500';
        return 'bg-emerald-500';
    };

    // Determine risk color
    const getRiskColor = (val) => {
        if (val >= 7) return 'bg-wine-600';
        if (val >= 4) return 'bg-amber-500';
        return 'bg-emerald-500';
    };

    const hasKinks = action.kinks && action.kinks.length > 0;

    return (
        <div className="h-full overflow-y-auto bg-dark-950">
            {/* Breadcrumb Navigation */}
            <div className="sticky top-0 z-30 bg-dark-950/95 backdrop-blur-sm border-b border-dark-800">
                <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-2 text-sm">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-1 text-dark-400 hover:text-white transition-colors group"
                    >
                        <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    </button>
                    <span className="text-dark-600">/</span>
                    <Link to="/actions" className="text-dark-400 hover:text-wine-400 transition-colors">Sex Acts</Link>
                    {action.parentVariant && (
                        <>
                            <ChevronRight className="w-4 h-4 text-dark-600" />
                            <Link to={`/action/${action.parentVariant.id}`} className="text-dark-400 hover:text-wine-400 transition-colors truncate max-w-xs">{action.parentVariant.name}</Link>
                        </>
                    )}
                    <ChevronRight className="w-4 h-4 text-dark-600" />
                    <span className="text-white font-medium truncate max-w-xs">{action?.name || 'Loading...'}</span>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                    {/* Left Column: Main Content */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Hero Header - Fantasy Theme */}
                        <div className="bg-[#0A0A0A]/80 border border-wine-800/30 rounded-2xl p-8 relative overflow-hidden">
                            {/* Ambient glow - fantasy colors */}
                            <div className="absolute top-0 right-0 w-96 h-96 bg-wine-700/15 rounded-full blur-3xl -mr-48 -mt-48 pointer-events-none" />
                            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#C4A35A]/8 rounded-full blur-3xl -ml-32 -mb-32 pointer-events-none" />

                            <div className="relative">
                                {/* Category & Type Badges */}
                                <div className="flex flex-wrap items-center gap-2 mb-4">
                                    <span className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-[0.15em] bg-wine-950/50 text-[#C4A35A]/80 border border-wine-700/40">
                                        {action.category || 'Action'}
                                    </span>
                                    <TypeBadge active={action.isPenetrative} label="Penetrative" color="bg-pink-500/20 text-pink-400 border border-pink-500/30" />
                                    <TypeBadge active={action.isOral} label="Oral" color="bg-rose-500/20 text-rose-400 border border-rose-500/30" />
                                    <TypeBadge active={action.isManual} label="Manual" color="bg-blue-500/20 text-blue-400 border border-blue-500/30" />
                                    <TypeBadge active={action.isImpact} label="Impact" color="bg-orange-500/20 text-orange-400 border border-orange-500/30" />
                                </div>

                                {/* Title - Fantasy Font */}
                                <h1 className="text-4xl md:text-5xl font-fantasy font-semibold text-cream/95 mb-4 tracking-wide">
                                    {action.name}
                                </h1>

                                {/* Gold Ornate Divider */}
                                <div className="ornate-divider w-32 mb-6" />

                                {/* Description */}
                                {action.description && (
                                    <div className="prose prose-lg prose-invert text-cream/60 prose-headings:text-cream/90 prose-strong:text-cream max-w-none leading-relaxed font-light">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{action.description}</ReactMarkdown>
                                    </div>
                                )}

                                {/* Stats Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8 pt-6 border-t border-dark-700/50">
                                    <StatMeter
                                        label="Intensity"
                                        // Use raw intensity (already 1-10) but formatted
                                        value={action.intensity?.toFixed(1)}
                                        scale={10}
                                        colorClass={getIntensityColor(action.intensity)}
                                    />
                                    <StatMeter
                                        label="Risk Level"
                                        // Standardize Risk to 1-5 scale (DB is 1-10)
                                        value={to5Scale((action.risk || 0) * 10)} // 1-10 -> 10-100 -> 1-5
                                        scale={5}
                                        colorClass={getRiskColor(action.risk)}
                                    />
                                    <div className="space-y-1.5">
                                        <div className="flex justify-between items-center text-sm font-medium">
                                            <span className="text-gray-300">Skill Required</span>
                                            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${action.skill === 'advanced' ? 'bg-purple-500/20 text-purple-400' :
                                                action.skill === 'intermediate' ? 'bg-blue-500/20 text-blue-400' :
                                                    'bg-dark-700 text-dark-300'
                                                }`}>
                                                {action.skill || 'beginner'}
                                            </span>
                                        </div>
                                        <div className="h-2.5 bg-dark-800 rounded-full overflow-hidden border border-dark-700">
                                            <div
                                                className={`h-full rounded-full transition-all duration-500 ${action.skill === 'advanced' ? 'bg-purple-500 w-full' :
                                                    action.skill === 'intermediate' ? 'bg-blue-500 w-2/3' :
                                                        'bg-dark-600 w-1/3'
                                                    }`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Roles Section */}
                        {(action.giverRole || action.receiverRole) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {action.giverRole && (
                                    <SectionCard title="For the Giver / Dominant" icon={Crown} iconColor="text-amber-400">
                                        {action.giverRole}
                                    </SectionCard>
                                )}
                                {action.receiverRole && (
                                    <SectionCard title="For the Receiver / Submissive" icon={Heart} iconColor="text-pink-400">
                                        {action.receiverRole}
                                    </SectionCard>
                                )}
                            </div>
                        )}

                        {/* Safety & Prerequisites */}
                        {(action.safetyNotes || action.prerequisites) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {action.prerequisites && (
                                    <SectionCard title="Prerequisites" icon={CheckCircle} iconColor="text-green-400">
                                        {action.prerequisites}
                                    </SectionCard>
                                )}
                                {action.safetyNotes && (
                                    <SectionCard title="Safety Notes" icon={Shield} iconColor="text-wine-400">
                                        {action.safetyNotes}
                                    </SectionCard>
                                )}
                            </div>
                        )}

                        {/* Physical Requirements */}
                        {action.physicalRequirements && (
                            <SectionCard title="Physical Requirements" icon={Activity} iconColor="text-orange-400">
                                {action.physicalRequirements}
                            </SectionCard>
                        )}

                        {/* Variations */}
                        {action.variations && (
                            <SectionCard title="Variations" icon={Sparkles} iconColor="text-purple-400">
                                {action.variations}
                            </SectionCard>
                        )}

                        {/* Dynamic Page Sections from database - renders ALL sections */}
                        {action.sections && Object.keys(action.sections).length > 0 && (
                            <div className="space-y-8">
                                <h2 className="text-xl font-display font-bold text-white flex items-center gap-3">
                                    <BookOpen className="w-5 h-5 text-wine-400" />
                                    Complete Guide
                                </h2>

                                <div className="space-y-3">
                                    {Object.entries(action.sections).map(([key, section]) => {
                                        const config = SECTION_CONFIG[key] || {
                                            label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                                            icon: Sparkles,
                                            color: 'text-gray-400'
                                        };
                                        const Icon = config.icon;

                                        return (
                                            <div key={key} id={`section-${key}`}>
                                                <SectionCard
                                                    title={config.label}
                                                    icon={Icon}
                                                    iconColor={config.color}
                                                    defaultOpen={false}
                                                >
                                                    <div className="prose prose-invert prose-sm max-w-none 
                                                        prose-headings:text-gray-100 prose-headings:font-semibold prose-headings:mt-6 prose-headings:mb-3
                                                        prose-h2:text-lg prose-h2:border-b prose-h2:border-dark-700/50 prose-h2:pb-2
                                                        prose-h3:text-base prose-h3:text-gray-200
                                                        prose-p:text-gray-300 prose-p:leading-relaxed
                                                        prose-strong:text-white prose-strong:font-semibold
                                                        prose-ul:text-gray-300 prose-ul:my-3 prose-li:my-1 prose-li:marker:text-gray-500
                                                        prose-table:border prose-table:border-dark-700 prose-table:rounded-lg prose-table:overflow-hidden
                                                        prose-thead:bg-dark-800/80 prose-th:text-gray-200 prose-th:font-semibold prose-th:px-4 prose-th:py-2.5 prose-th:text-left
                                                        prose-td:px-4 prose-td:py-2.5 prose-td:border-t prose-td:border-dark-700/50 prose-td:text-gray-300
                                                        prose-tr:hover:bg-dark-800/30 prose-tr:transition-colors
                                                        prose-a:text-red-400 prose-a:no-underline hover:prose-a:text-red-300 hover:prose-a:underline
                                                        prose-hr:border-dark-700/50 prose-hr:my-6
                                                    ">
                                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                            {section.content || ''}
                                                        </ReactMarkdown>
                                                    </div>
                                                </SectionCard>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}


                        {/* Who Performs This? - Archetype Frequencies */}
                        {action.archetypes && action.archetypes.length > 0 && (
                            <div className="glass-card p-8 border-t-2 border-t-amber-500/30">
                                <h3 className="text-xl font-display font-bold text-white mb-6 flex items-center gap-3">
                                    <Crown className="w-6 h-6 text-amber-500" />
                                    Who Performs This?
                                </h3>

                                {/* High Frequency (Always/Often) */}
                                {action.archetypes.some(a => ['always', 'often'].includes(a.ArchetypeSexAction?.frequency)) && (
                                    <div className="mb-6">
                                        <h4 className="text-sm font-bold uppercase tracking-wider text-amber-400 mb-3 flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-amber-500"></span> Common For
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {action.archetypes.filter(a => ['always', 'often'].includes(a.ArchetypeSexAction?.frequency)).map(archetype => (
                                                <Link key={archetype.id} to={`/archetype/${archetype.id}`} className="flex items-center gap-3 p-3 rounded-lg bg-dark-800/50 hover:bg-dark-800 border border-transparent hover:border-amber-500/30 transition-all group">
                                                    <span className="text-2xl">{archetype.icon}</span>
                                                    <div>
                                                        <div className="font-bold text-gray-200 group-hover:text-amber-300">{archetype.name}</div>
                                                        <div className="text-xs text-gray-500 px-1.5 py-0.5 rounded bg-dark-900/50 inline-block mt-1">
                                                            {archetype.ArchetypeSexAction.frequency}
                                                        </div>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Lower Frequency (Sometimes/Rarely) */}
                                {action.archetypes.some(a => ['sometimes', 'rarely'].includes(a.ArchetypeSexAction?.frequency)) && (
                                    <div>
                                        <h4 className="text-sm font-bold uppercase tracking-wider text-blue-400 mb-3 flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-blue-500"></span> Occasionally For
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                            {action.archetypes.filter(a => ['sometimes', 'rarely'].includes(a.ArchetypeSexAction?.frequency)).map(archetype => (
                                                <Link key={archetype.id} to={`/archetype/${archetype.id}`} className="flex items-center gap-3 p-3 rounded-lg bg-dark-800/50 hover:bg-dark-800 border border-transparent hover:border-blue-500/30 transition-all group">
                                                    <span className="text-2xl">{archetype.icon}</span>
                                                    <div>
                                                        <div className="font-bold text-gray-200 group-hover:text-blue-300">{archetype.name}</div>
                                                        <div className="text-xs text-gray-500 px-1.5 py-0.5 rounded bg-dark-900/50 inline-block mt-1">
                                                            {archetype.ArchetypeSexAction.frequency}
                                                        </div>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Sub-Variants */}
                        {action.childVariants && action.childVariants.length > 0 && (
                            <div className="glass-card p-8 border-t-2 border-t-indigo-500/30">
                                <h3 className="text-xl font-display font-bold text-white mb-6 flex items-center gap-3">
                                    <Layers className="w-6 h-6 text-indigo-400" />
                                    Variants of {action.name}
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {action.childVariants.map(variant => (
                                        <Link
                                            key={variant.id}
                                            to={`/action/${variant.id}`}
                                            className="flex items-start gap-4 p-4 rounded-xl bg-dark-900/50 border border-dark-800 hover:border-indigo-500/50 hover:bg-dark-800/50 transition-all group"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <h4 className="font-bold text-dark-100 group-hover:text-white transition-colors truncate">{variant.name}</h4>
                                                {variant.category && <div className="text-sm text-dark-400">{variant.category}</div>}
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-dark-600 group-hover:text-indigo-400 transition-colors shrink-0 mt-1" />
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Linked Kinks Section */}
                        <div>
                            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                                <Layers className="w-6 h-6 text-indigo-400" />
                                Linked Kinks
                                {hasKinks && (
                                    <span className="text-sm font-normal text-dark-400">
                                        ({action.kinks.length})
                                    </span>
                                )}
                            </h2>

                            {hasKinks ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {action.kinks.map(kink => (
                                        <Link
                                            key={kink.id}
                                            to={`/kink/${kink.id}`}
                                            className="flex items-start gap-4 p-5 rounded-xl bg-dark-900/50 border border-dark-800 hover:border-indigo-500/50 hover:bg-dark-800/50 transition-all group"
                                        >
                                            <div className="mt-1 p-2.5 rounded-lg bg-dark-950 border border-dark-800 group-hover:border-indigo-500/30 transition-colors">
                                                <Crown className="w-5 h-5 text-dark-400 group-hover:text-indigo-400 transition-colors" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-bold text-lg text-dark-100 group-hover:text-white transition-colors truncate">
                                                    {kink.name}
                                                </h3>
                                                <div className="text-sm text-dark-400 mb-1">{kink.category}</div>
                                                {kink.summary && (
                                                    <p className="text-xs text-dark-500 line-clamp-2">{kink.summary}</p>
                                                )}
                                                {kink.KinkSexAction?.note && (
                                                    <div className="text-xs text-indigo-300/80 italic flex items-center gap-1 mt-2">
                                                        <Sparkles className="w-3 h-3" /> {kink.KinkSexAction.note}
                                                    </div>
                                                )}
                                            </div>
                                            <ExternalLink className="w-4 h-4 text-dark-600 group-hover:text-indigo-400 transition-colors shrink-0" />
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-8 text-center text-dark-500 border border-dashed border-dark-700 rounded-xl">
                                    No kinks linked to this action yet.
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column: Jump To Sidebar */}
                    <div className="lg:col-span-1 hidden lg:block">
                        <div className="sticky top-24 space-y-6 max-h-[calc(100vh-8rem)] overflow-y-auto no-scrollbar">
                            {/* Back to Actions */}
                            <button
                                onClick={() => navigate('/actions')}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-dark-800/80 hover:bg-dark-700 border border-dark-700 hover:border-wine-500/30 transition-all text-gray-300 hover:text-white group"
                            >
                                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                                <span className="text-sm font-medium">Back to Actions</span>
                            </button>

                            {/* Quick Index - Dynamic section list */}
                            {action.sections && Object.keys(action.sections).length > 0 && (
                                <div className="glass-card p-4">
                                    <h4 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-4">Jump To</h4>
                                    <div className="space-y-0.5">
                                        {Object.keys(action.sections).map(key => {
                                            const config = SECTION_CONFIG[key] || {
                                                label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                                                icon: Sparkles,
                                                color: 'text-gray-400'
                                            };
                                            const Icon = config.icon;
                                            return (
                                                <button
                                                    key={key}
                                                    onClick={() => {
                                                        const el = document.getElementById(`section-${key}`);
                                                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                    }}
                                                    className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-left text-sm transition-all hover:bg-dark-700/50 text-gray-400 hover:text-white"
                                                >
                                                    <Icon className={`w-3 h-3 ${config.color}`} />
                                                    <span className="break-words text-sm">{config.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Linked Kinks Summary */}
                            {hasKinks && (
                                <div className="glass-card p-4">
                                    <h4 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-3">Related Kinks</h4>
                                    <div className="space-y-1">
                                        {action.kinks.slice(0, 5).map(kink => (
                                            <Link
                                                key={kink.id}
                                                to={`/kink/${kink.id}`}
                                                className="block px-3 py-2 rounded-md text-sm text-gray-400 hover:text-white hover:bg-dark-700/50 transition-colors truncate"
                                            >
                                                {kink.name}
                                            </Link>
                                        ))}
                                        {action.kinks.length > 5 && (
                                            <span className="block px-3 py-1 text-xs text-gray-500">+{action.kinks.length - 5} more</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SexActionPage;
